import React, { useState } from "react";
import useComments from "hooks/predictions/useComments";
import useAuth from "hooks/auth/useAuth";
import CommentItem from "./CommentItem";
import SendIcon from "@mui/icons-material/Send";
import useDarkMode from "hooks/useDarkMode";

const CommentSection = ({ docPath, isInModal = false }) => {
  const { currentUser } = useAuth();
  const { comments, loading, addComment } = useComments(docPath);
  const [newComment, setNewComment] = useState("");
  const { theme } = useDarkMode();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newComment.trim() || !currentUser) return;
    addComment(currentUser, newComment);
    setNewComment("");
  };

  const handleReply = (text, parentId, replyToUserName) => {
    if (!currentUser) return;
    addComment(currentUser, text, parentId, replyToUserName);
  };

  // Organize comments into threads
  const topLevelComments = comments.filter((c) => !c.parentId);
  const replies = comments.filter((c) => c.parentId);

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col h-full gap-4 px-3 pt-3 pb-4 md:p-5 ${
        !isInModal
          ? "bg-black/40 border-t border-cyan-500/10 light:bg-white/60 light:border-slate-200 light:shadow-sm rounded-b-[24px]"
          : "light:bg-slate-50/30"
      }`}
    >
      <div className="flex-1 flex flex-col gap-3 min-h-0">
        {!isInModal && (
          <h3 className="text-cyan-50/70 light:text-slate-500 text-[11px] font-bold tracking-widest uppercase mb-1">
            Մեկնաբանություններ ({comments.length})
          </h3>
        )}

        {topLevelComments.length > 0 ? (
          <div
            className={`flex-1 overflow-y-auto pr-2 custom-scrollbar ${
              isInModal ? "pb-[160px] md:pb-24" : "pb-4"
            }`}
          >
            <div className="flex flex-col gap-6">
              {topLevelComments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  docPath={docPath}
                  onReply={handleReply}
                  replies={replies.filter((r) => r.parentId === comment.id)}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-slate-500 light:text-slate-400 text-sm text-center py-4 italic">
            Դեռևս մեկնաբանություններ չկան:
          </p>
        )}
      </div>

      {currentUser ? (
        <form
          onSubmit={handleSubmit}
          className={`flex flex-row gap-3 items-center ${
            isInModal
              ? "absolute bottom-0 left-0 w-full p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] mb-[68px] md:mb-0 bg-[#030014]/95 light:bg-white/95 backdrop-blur-md border-t border-cyan-500/10 light:border-slate-200 z-10 shadow-[0_-10px_30px_rgba(0,0,0,0.5)] md:shadow-none"
              : "relative mt-2"
          }`}
        >
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Գրեք մեկնաբանություն..."
            className="flex-1 bg-white/5 border-cyan-500/20 text-cyan-50 light:bg-slate-100 light:border-slate-200 light:text-slate-900 border rounded-2xl px-5 py-3 text-sm focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/20 transition-all placeholder:text-slate-500"
          />
          <button
            type="submit"
            disabled={!newComment.trim()}
            className="bg-cyan-500 hover:bg-cyan-400 disabled:bg-white/5 disabled:border-white/5 disabled:text-slate-600 text-white w-[46px] h-[46px] rounded-2xl transition-all flex items-center justify-center shadow-[0_0_15px_rgba(102,133,255,0.2)] hover:shadow-[0_0_20px_rgba(102,133,255,0.4)]"
          >
            <SendIcon sx={{ fontSize: 20 }} />
          </button>
        </form>
      ) : (
        <div
          className={`text-center p-4 bg-white/5 border-white/10 light:bg-slate-50 light:border-slate-200 border ${
            isInModal
              ? "absolute bottom-0 left-0 w-full mb-[68px] md:mb-0 rounded-none border-x-0 border-b-0 pb-[calc(1rem+env(safe-area-inset-bottom))]"
              : "rounded-2xl mt-2"
          }`}
        >
          <p className="text-slate-400 light:text-slate-600 text-sm">
            Մեկնաբանելու համար անհրաժեշտ է{" "}
            <span
              className="text-cyan-400 font-bold cursor-pointer hover:text-cyan-300 transition-colors"
              onClick={() => (window.location.href = "/profile")}
            >
              մուտք գործել
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default CommentSection;
